/**
 * @file resolveAttachmentVersionOrigin.ts
 * @description Names where one attachment version came from, and holds the
 * labels the panel prints for each answer. Consumers: `AttachmentList`'s file
 * row and `AttachmentVersionRow`'s strip entry.
 *
 * `layout === 'chunked'` is the only honest Desktop discriminator, and this is
 * the one place that knowledge lives. Layout is derived server-side from the
 * reservation's kind — `save` gives chunked, `published` gives object — and
 * survives publishing untouched. Three near-misses a reader will reach for:
 * `kind` is not it, because a published desktop version reads `'published'`;
 * `md5Hash` is not it, because both lanes carry one; `meta.agentType` is not
 * it, because `commitAttachmentVersion.ts` hardcodes `AgentType.User` for the
 * agent and the browser alike. The premise the discriminator rests on: the
 * agent always reserves `kind: 'save'`. An agent reservation of `published`
 * would be laid out as one object and read here as "Uploaded here".
 */

import type { AttachmentVersion } from '#types/System.types';

export type AttachmentVersionOrigin = 'email' | 'aiExtract' | 'desktop' | 'uploadedHere';

const JSON_FILE_NAME_RE = /\.json$/i;

const isJsonAttachmentFileName = (fileName: string | undefined | null): boolean => {
    if (typeof fileName !== 'string' || fileName.trim().length === 0) {
        return false;
    }
    return JSON_FILE_NAME_RE.test(fileName.trim());
};

export const attachmentVersionOriginLabels: Record<AttachmentVersionOrigin, string> = {
    email: 'Email',
    aiExtract: 'AI Extract',
    desktop: 'Desktop',
    uploadedHere: 'Uploaded here',
};

export const resolveAttachmentVersionOrigin = (
    attachmentMeta: Record<string, unknown> | undefined,
    version: AttachmentVersion,
): AttachmentVersionOrigin => {
    if (version.emailOriginRecordId) {
        const aiProvenanceLabel = typeof attachmentMeta?.aiProvenanceLabel === 'string'
            ? attachmentMeta.aiProvenanceLabel.trim()
            : '';
        const isAiIngestionJson = isJsonAttachmentFileName(version.versionFileName)
            && (aiProvenanceLabel === 'AI Extract' || aiProvenanceLabel === 'AI Source');
        return isAiIngestionJson ? 'aiExtract' : 'email';
    }
    if (version.layout === 'chunked') {
        return 'desktop';
    }
    return 'uploadedHere';
};
